import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, Eye, Crown, Ban, Trash2 } from "lucide-react";
import { formatDate } from "@/lib/format";

const ROLE_LABEL = {
  owner: { label: "Eigenaar", icon: Crown, color: "bg-amber-50 text-amber-700 border-amber-200" },
  viewer: { label: "Lezer", icon: Eye, color: "bg-sky-50 text-sky-700 border-sky-200" },
  pending: { label: "Wacht op goedkeuring", icon: null, color: "bg-slate-100 text-slate-600 border-slate-200" },
  blocked: { label: "Geblokkeerd", icon: Ban, color: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/users").then(({ data }) => setUsers(data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setRole = async (user_id, role) => {
    try {
      await api.patch(`/users/${user_id}/role`, { role });
      toast.success("Rol bijgewerkt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update mislukt");
    }
  };
  const del = async (user_id) => {
    if (!window.confirm("Gebruiker verwijderen?")) return;
    try {
      await api.delete(`/users/${user_id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <header>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Toegangsbeheer</p>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mt-1">Gebruikers</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Beheer wie toegang heeft tot de Lifjalla data. Lezers zien alle dashboards maar kunnen niets wijzigen.
        </p>
      </header>

      <Card className="bg-white border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                <th className="text-left py-3 px-4 font-semibold">Gebruiker</th>
                <th className="text-left py-3 px-4 font-semibold">E-mail</th>
                <th className="text-left py-3 px-4 font-semibold">Rol</th>
                <th className="text-left py-3 px-4 font-semibold">Aangemaakt</th>
                <th className="text-left py-3 px-4 font-semibold">Laatste login</th>
                <th className="text-right py-3 px-4 font-semibold">Acties</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="text-center text-slate-400 py-8">Laden…</td></tr>}
              {!loading && users.length === 0 && (
                <tr><td colSpan="6" className="text-center text-slate-400 py-8">Geen gebruikers.</td></tr>
              )}
              {users.map((u) => {
                const r = ROLE_LABEL[u.role] || ROLE_LABEL.pending;
                return (
                  <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2">
                        {u.picture ? <img src={u.picture} alt="" className="h-8 w-8 rounded-full" /> : <div className="h-8 w-8 rounded-full bg-slate-200" />}
                        <span className="font-medium text-slate-900">{u.name || "—"}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-slate-700">{u.email}</td>
                    <td className="py-2.5 px-4">
                      <Badge variant="outline" className={r.color}>
                        {r.icon && <r.icon className="h-3 w-3 mr-1" />}
                        {r.label}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-4 text-slate-500 text-xs">{formatDate(u.created_at)}</td>
                    <td className="py-2.5 px-4 text-slate-500 text-xs">{formatDate(u.last_login_at)}</td>
                    <td className="py-2.5 px-4 text-right">
                      <div className="flex gap-1 justify-end">
                        {u.role === "pending" && (
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-emerald-700" onClick={() => setRole(u.user_id, "viewer")} data-testid={`approve-${u.user_id}`}>
                            <Check className="h-3.5 w-3.5 mr-1" /> Goedkeuren
                          </Button>
                        )}
                        {u.role === "viewer" && (
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-600" onClick={() => setRole(u.user_id, "blocked")} data-testid={`block-${u.user_id}`}>
                            <Ban className="h-3.5 w-3.5 mr-1" /> Blokkeer
                          </Button>
                        )}
                        {u.role === "blocked" && (
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-emerald-700" onClick={() => setRole(u.user_id, "viewer")} data-testid={`unblock-${u.user_id}`}>
                            <Check className="h-3.5 w-3.5 mr-1" /> Deblokkeren
                          </Button>
                        )}
                        {u.role !== "owner" && (
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-600" onClick={() => del(u.user_id)} data-testid={`delete-user-${u.user_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
